import React, { useEffect, useRef, useState } from "react";
import { HashLink } from "react-router-hash-link";
import {
  Container,
  LogoContainer,
  Navigation,
  Links,
  Link,
  SidebarContainer,
  SidebarBackdrop,
  Sidebar,
  SidebarFooter,
  SidebarSocials,
  SidebarSocialLink,
  SidebarEmail,
} from "./Header.Styled";
import logo from "../../assets/logo-320.webp";
import { useNavigate } from "react-router-dom";
import { MediaQuery, useDeviceType } from "../../hooks/useDeviceType";
import { AnimatePresence, motion } from "framer-motion";
import {
  navigationVariants,
  navigationItemVariants,
  sidebarVariants,
  parentVariants,
  commonDownAppearVariants,
} from "../../animations/motion";
import { MenuToggle } from "./MenuToggle";
import { FiGithub, FiInstagram, FiLinkedin } from "react-icons/fi";

const Header = () => {
  const [showHeader, setShowHeader] = useState(true);
  const [isTop, setIsTop] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const headerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const firstMenuLinkRef = useRef(null);

  const isDesktop = useDeviceType(MediaQuery.isDesktop);
  const navigate = useNavigate();

  function toggleMenu() {
    setIsOpen((open) => !open);
  }

  function closeMenuAndRestoreFocus() {
    setIsOpen(false);
    window.requestAnimationFrame(() => menuButtonRef.current?.focus());
  }

  function handleMenuKeyDown(event) {
    if (!isOpen) return;

    if (event.key === "Escape") {
      event.preventDefault();
      closeMenuAndRestoreFocus();
      return;
    }

    if (event.key !== "Tab") return;

    const focusableElements = event.currentTarget.querySelectorAll(
      'a[href], button:not([disabled]):not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement?.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement?.focus();
    }
  }

  function handleLogoClick() {
    navigate("/");
    window.scrollTo({ top: 0 });
  }

  function handleHeaderFocus(event) {
    if (event.target.matches(":focus-visible")) {
      setShowHeader(true);
    }
  }

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateHeader = () => {
      const scrollTop = window.scrollY;
      const scrollDifference = scrollTop - lastScrollY;
      const focusedElement = document.activeElement;
      const hasKeyboardFocus =
        headerRef.current?.contains(focusedElement) &&
        focusedElement?.matches(":focus-visible");

      setIsTop(scrollTop < 8);

      if (hasKeyboardFocus || scrollTop < 80 || scrollDifference < -8) {
        setShowHeader(true);
      } else if (scrollDifference > 8) {
        setShowHeader(false);
      }

      lastScrollY = scrollTop;
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousDocumentOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    const pageShell = document.getElementById("page-shell");
    const previousPageShellAriaHidden = pageShell?.getAttribute("aria-hidden");
    const previousPageShellInert = pageShell?.inert;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    if (pageShell) {
      pageShell.inert = true;
      pageShell.setAttribute("aria-hidden", "true");
    }
    window.requestAnimationFrame(() => firstMenuLinkRef.current?.focus());

    return () => {
      document.documentElement.style.overflow = previousDocumentOverflow;
      document.body.style.overflow = previousBodyOverflow;
      if (pageShell) {
        pageShell.inert = Boolean(previousPageShellInert);
        if (previousPageShellAriaHidden === null) {
          pageShell.removeAttribute("aria-hidden");
        } else {
          pageShell.setAttribute("aria-hidden", previousPageShellAriaHidden);
        }
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isDesktop && isOpen) {
      setIsOpen(false);
    }
  }, [isDesktop, isOpen]);

  return (
    <Container
      ref={headerRef}
      onFocusCapture={handleHeaderFocus}
      $isTop={isTop}
      $showHeader={showHeader}
      as={motion.header}
      initial="closed"
      animate="open"
      variants={parentVariants}
    >
      <LogoContainer
        type="button"
        aria-label="Back to the top"
        onClick={handleLogoClick}
        as={motion.button}
        variants={commonDownAppearVariants}
      >
        <img
          src={logo}
          alt=""
          width="1042"
          height="330"
          fetchpriority="high"
        />
      </LogoContainer>
      {isDesktop ? (
        <Navigation
          as={motion.nav}
          initial="closed"
          animate="open"
          variants={parentVariants}
        >
          <Links>
            <Link as={motion.li} variants={commonDownAppearVariants}>
              01.
              <HashLink to="/#About">About</HashLink>
            </Link>
            <Link as={motion.li} variants={commonDownAppearVariants}>
              02.
              <HashLink to="/#Experience">Experience</HashLink>
            </Link>
            <Link as={motion.li} variants={commonDownAppearVariants}>
              03.
              <HashLink to="/#Work">Work</HashLink>
            </Link>
            <Link as={motion.li} variants={commonDownAppearVariants}>
              04.
              <HashLink to="/#Contact">Contact</HashLink>
            </Link>
          </Links>
        </Navigation>
      ) : (
        <SidebarContainer $isOpen={isOpen} onKeyDown={handleMenuKeyDown}>
          <AnimatePresence>
            {isOpen ? (
              <SidebarBackdrop
                as={motion.button}
                type="button"
                aria-hidden="true"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                tabIndex={-1}
                onClick={closeMenuAndRestoreFocus}
              />
            ) : null}
          </AnimatePresence>
          <Sidebar
            id="mobile-navigation"
            as={motion.div}
            initial={false}
            animate={isOpen ? "open" : "closed"}
            variants={sidebarVariants}
            aria-hidden={!isOpen}
            inert={!isOpen ? "" : undefined}
            aria-label="Site navigation"
            aria-modal={isOpen ? "true" : undefined}
            role="dialog"
          >
            <AnimatePresence>
              {isOpen ? (
              <Navigation
                as={motion.nav}
                initial="closed"
                animate="open"
                exit="closed"
                variants={navigationVariants}
              >
                <Links>
                  <Link as={motion.li} variants={navigationItemVariants}>
                    01.
                    <HashLink
                      ref={firstMenuLinkRef}
                      to="/#About"
                      onClick={closeMenuAndRestoreFocus}
                    >
                      About
                    </HashLink>
                  </Link>
                  <Link as={motion.li} variants={navigationItemVariants}>
                    02.
                    <HashLink
                      to="/#Experience"
                      onClick={closeMenuAndRestoreFocus}
                    >
                      Experience
                    </HashLink>
                  </Link>
                  <Link as={motion.li} variants={navigationItemVariants}>
                    03.
                    <HashLink to="/#Work" onClick={closeMenuAndRestoreFocus}>
                      Work
                    </HashLink>
                  </Link>
                  <Link as={motion.li} variants={navigationItemVariants}>
                    04.
                    <HashLink
                      to="/#Contact"
                      onClick={closeMenuAndRestoreFocus}
                    >
                      Contact
                    </HashLink>
                  </Link>
                </Links>
                <SidebarFooter as={motion.div} variants={navigationItemVariants}>
                  <SidebarSocials aria-label="Social links">
                    <SidebarSocialLink
                      href="https://github.com/VietHoang37"
                      rel="noopener noreferrer"
                      target="_blank"
                      aria-label="GitHub"
                    >
                      <FiGithub aria-hidden="true" />
                    </SidebarSocialLink>
                    <SidebarSocialLink
                      href="https://instagram.com/viet_hoang_ng"
                      rel="noopener noreferrer"
                      target="_blank"
                      aria-label="Instagram"
                    >
                      <FiInstagram aria-hidden="true" />
                    </SidebarSocialLink>
                    <SidebarSocialLink
                      href="https://www.linkedin.com/in/viet-hoang-nguyen-591212226/"
                      rel="noopener noreferrer"
                      target="_blank"
                      aria-label="LinkedIn"
                    >
                      <FiLinkedin aria-hidden="true" />
                    </SidebarSocialLink>
                  </SidebarSocials>
                  <SidebarEmail href="mailto:viethoang.ng7@gmail.com">
                    viethoang.ng7@gmail.com
                  </SidebarEmail>
                </SidebarFooter>
              </Navigation>
              ) : null}
            </AnimatePresence>
          </Sidebar>
          <MenuToggle
            buttonRef={menuButtonRef}
            toggle={toggleMenu}
            isOpen={isOpen}
          />
        </SidebarContainer>
      )}
    </Container>
  );
};

export default Header;
